import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getLevelRewards, setLevelReward, deleteLevelReward } from './adminService';
import {
  Trophy,
  Gift,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Star,
  DollarSign
} from 'lucide-react';

export default function AdminRewardsPage() {
  const { isArabic } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [levels, setLevels] = useState([]);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [filterHasReward, setFilterHasReward] = useState('all'); // 'all', 'with', 'without'

  // Form state for adding/editing rewards
  const [rewardForm, setRewardForm] = useState({
    level: null,
    reward_type: '1_bd_coupon',
    reward_value: '',
    reward_label: '',
    is_active: true
  });

  // Load rewards on mount
  useEffect(() => {
    loadLevelRewards();
  }, []);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  async function loadLevelRewards() {
    try {
      setLoading(true);
      setError(null);
      const data = await getLevelRewards();
      setLevels(data.levels || []);
    } catch (err) {
      console.error('Error loading level rewards:', err);
      setError(err.message || 'Failed to load level rewards');
    } finally {
      setLoading(false);
    }
  }

  // Open modal to add/edit reward
  function openRewardModal(level) {
    setEditingLevel(level);
    if (level.reward_type) {
      // Editing existing reward
      setRewardForm({
        level: level.level,
        reward_type: level.reward_type,
        reward_value: level.reward_value || '',
        reward_label: level.reward_label || '',
        is_active: level.is_active !== false
      });
    } else {
      // Adding new reward
      setRewardForm({
        level: level.level,
        reward_type: '1_bd_coupon',
        reward_value: '',
        reward_label: '',
        is_active: true
      });
    }
    setShowRewardModal(true);
  }

  // Close modal
  function closeRewardModal() {
    setShowRewardModal(false);
    setEditingLevel(null);
    setRewardForm({
      level: null,
      reward_type: '1_bd_coupon',
      reward_value: '',
      reward_label: '',
      is_active: true
    });
  }

  // Save reward
  async function handleSaveReward() {
    try {
      setError(null);

      // Validate
      if (rewardForm.reward_type === 'custom' && !rewardForm.reward_value) {
        setError(isArabic ? 'يرجى إدخال قيمة المكافأة المخصصة' : 'Please enter custom reward value');
        return;
      }

      const rewardData = {
        reward_type: rewardForm.reward_type,
        reward_value: rewardForm.reward_value ? parseFloat(rewardForm.reward_value) : null,
        reward_label: rewardForm.reward_label || null,
        is_active: rewardForm.is_active
      };

      await setLevelReward(rewardForm.level, rewardData);
      setSuccess(isArabic ? `تم حفظ مكافأة المستوى ${rewardForm.level} بنجاح` : `Level ${rewardForm.level} reward saved successfully`);
      closeRewardModal();
      loadLevelRewards();
    } catch (err) {
      console.error('Error saving reward:', err);
      setError(err.message || 'Failed to save reward');
    }
  }

  // Delete reward
  async function handleDeleteReward(level) {
    if (!window.confirm(isArabic ? `هل تريد حذف مكافأة المستوى ${level}؟` : `Delete reward for level ${level}?`)) {
      return;
    }

    try {
      setError(null);
      await deleteLevelReward(level);
      setSuccess(isArabic ? `تم حذف مكافأة المستوى ${level}` : `Level ${level} reward deleted`);
      loadLevelRewards();
    } catch (err) {
      console.error('Error deleting reward:', err);
      setError(err.message || 'Failed to delete reward');
    }
  }

  // Get reward type label
  function getRewardTypeLabel(type) {
    const labels = {
      '1_bd_coupon': isArabic ? 'كوبون 1 دينار' : '1 BD Coupon',
      '2_bd_coupon': isArabic ? 'كوبون 2 دينار' : '2 BD Coupon',
      'free_session': isArabic ? 'جلسة مجانية' : 'Free Session',
      'bogo': isArabic ? 'اشترِ واحدة واحصل على الثانية' : 'BOGO',
      '20_percent_discount': isArabic ? 'خصم 20%' : '20% Discount',
      'custom': isArabic ? 'مكافأة مخصصة' : 'Custom Reward'
    };
    return labels[type] || type;
  }

  // Get reward icon
  function getRewardIcon(type) {
    switch(type) {
      case '1_bd_coupon':
      case '2_bd_coupon':
        return <DollarSign size={18} />;
      case 'free_session':
        return <Gift size={18} />;
      case 'bogo':
        return <Trophy size={18} />;
      case '20_percent_discount':
        return <Star size={18} />;
      default:
        return <Gift size={18} />;
    }
  }

  // Filter levels
  const filteredLevels = levels.filter(level => {
    if (filterHasReward === 'with') return level.reward_type;
    if (filterHasReward === 'without') return !level.reward_type;
    return true;
  });

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <Trophy size={32} color="#FF7426" />
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#232B3E', margin: 0 }}>
            {isArabic ? 'إدارة مكافآت المستويات' : 'Level Rewards Management'}
          </h1>
        </div>
        <p style={{ fontSize: 16, color: '#64748b', margin: 0 }}>
          {isArabic
            ? 'قم بتكوين المكافآت لكل من المستويات الـ 100. سيحصل الطلاب على هذه المكافآت عند الوصول إلى المستوى المحدد.'
            : 'Configure rewards for each of the 100 levels. Students will receive these rewards when they reach the specified level.'
          }
        </p>
        <p style={{ fontSize: 14, color: '#FF7426', fontWeight: 600, marginTop: 12 }}>
          {isArabic
            ? '⭐ المستوى 100 يمنح خصم 20% دائم على جميع الحجوزات المستقبلية تلقائياً'
            : '⭐ Level 100 grants a permanent 20% discount on all future bookings automatically'
          }
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div style={{
          padding: '16px 20px',
          background: '#D1FAE5',
          border: '2px solid #6EE7B7',
          borderRadius: 12,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: '#059669'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: 600 }}>{success}</span>
        </div>
      )}

      {error && (
        <div style={{
          padding: '16px 20px',
          background: '#FEE2E2',
          border: '2px solid #FCA5A5',
          borderRadius: 12,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: '#DC2626'
        }}>
          <AlertCircle size={20} />
          <span style={{ fontWeight: 600 }}>{error}</span>
        </div>
      )}

      {/* Filter Controls */}
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: '20px',
        marginBottom: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#232B3E' }}>
          {isArabic ? 'تصفية:' : 'Filter:'}
        </span>
        <select
          value={filterHasReward}
          onChange={(e) => setFilterHasReward(e.target.value)}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: '2px solid #e0eaff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            background: '#fff',
            color: '#232B3E',
            outline: 'none'
          }}
        >
          <option value="all">{isArabic ? 'جميع المستويات' : 'All Levels'}</option>
          <option value="with">{isArabic ? 'مع مكافآت' : 'With Rewards'}</option>
          <option value="without">{isArabic ? 'بدون مكافآت' : 'Without Rewards'}</option>
        </select>
        <span style={{ fontSize: 14, color: '#64748b', marginLeft: 'auto' }}>
          {isArabic
            ? `عرض ${filteredLevels.length} من ${levels.length} مستوى`
            : `Showing ${filteredLevels.length} of ${levels.length} levels`
          }
        </span>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #FF7426',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748b', fontSize: 15 }}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      )}

      {/* Levels Grid */}
      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16
        }}>
          {filteredLevels.map(level => (
            <div
              key={level.level}
              style={{
                background: level.reward_type ? 'linear-gradient(135deg, #fff 0%, #f0f9ff 100%)' : '#fff',
                borderRadius: 16,
                padding: '20px',
                boxShadow: level.reward_type ? '0 4px 12px rgba(255, 116, 38, 0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
                border: level.reward_type ? '2px solid #FF7426' : '2px solid #e0eaff',
                transition: 'all 0.2s',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={() => openRewardModal(level)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = level.reward_type
                  ? '0 6px 16px rgba(255, 116, 38, 0.2)'
                  : '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = level.reward_type
                  ? '0 4px 12px rgba(255, 116, 38, 0.1)'
                  : '0 2px 8px rgba(0,0,0,0.05)';
              }}
            >
              {/* Level Number */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12
              }}>
                <div style={{
                  background: level.reward_type ? '#FF7426' : '#e0eaff',
                  color: level.reward_type ? '#fff' : '#232B3E',
                  fontWeight: 800,
                  fontSize: 18,
                  padding: '8px 16px',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  {level.level === 100 && <Star size={16} />}
                  {isArabic ? `المستوى ${level.level}` : `Level ${level.level}`}
                </div>
                {level.reward_type && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteReward(level.level);
                    }}
                    style={{
                      background: '#FEE2E2',
                      color: '#DC2626',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FCA5A5'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#FEE2E2'}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Reward Info */}
              {level.reward_type ? (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                    color: '#FF7426',
                    fontWeight: 700
                  }}>
                    {getRewardIcon(level.reward_type)}
                    <span>{getRewardTypeLabel(level.reward_type)}</span>
                  </div>
                  {level.reward_label && (
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0' }}>
                      {level.reward_label}
                    </p>
                  )}
                  {level.reward_value && (
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0' }}>
                      {isArabic ? 'القيمة:' : 'Value:'} {level.reward_value} {isArabic ? 'دينار' : 'BD'}
                    </p>
                  )}
                  {level.level === 100 && (
                    <div style={{
                      background: '#FEF3C7',
                      border: '1px solid #FCD34D',
                      borderRadius: 8,
                      padding: '8px 12px',
                      marginTop: 12,
                      fontSize: 12,
                      color: '#92400E',
                      fontWeight: 600
                    }}>
                      ⭐ {isArabic ? 'خصم دائم 20%' : 'Permanent 20% discount'}
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 12,
                    fontSize: 12,
                    color: level.is_active === false ? '#DC2626' : '#059669',
                    fontWeight: 600
                  }}>
                    {level.is_active === false ? '⚠️ Inactive' : '✓ Active'}
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '20px 0',
                  color: '#94a3b8',
                  fontSize: 14
                }}>
                  <Plus size={32} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {isArabic ? 'لا توجد مكافأة' : 'No reward'}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: 12 }}>
                    {isArabic ? 'انقر لإضافة' : 'Click to add'}
                  </p>
                </div>
              )}

              {/* Edit Icon */}
              <div style={{
                position: 'absolute',
                bottom: 12,
                right: 12,
                background: '#FF7426',
                color: '#fff',
                borderRadius: 8,
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8
              }}>
                {level.reward_type ? <Edit size={14} /> : <Plus size={14} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reward Modal */}
      {showRewardModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}
        onClick={closeRewardModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              padding: '32px',
              maxWidth: 600,
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: '#232B3E', margin: 0 }}>
                {isArabic
                  ? `${editingLevel?.reward_type ? 'تعديل' : 'إضافة'} مكافأة المستوى ${rewardForm.level}`
                  : `${editingLevel?.reward_type ? 'Edit' : 'Add'} Level ${rewardForm.level} Reward`
                }
              </h2>
              <button
                onClick={closeRewardModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={24} color="#232B3E" />
              </button>
            </div>

            {/* Reward Type */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 700, color: '#232B3E' }}>
                {isArabic ? 'نوع المكافأة' : 'Reward Type'}
              </label>
              <select
                value={rewardForm.reward_type}
                onChange={(e) => setRewardForm({ ...rewardForm, reward_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '2px solid #e0eaff',
                  fontSize: 15,
                  fontWeight: 600,
                  outline: 'none',
                  cursor: 'pointer',
                  background: '#fff',
                  color: '#232B3E'
                }}
              >
                <option value="1_bd_coupon">{isArabic ? 'كوبون 1 دينار' : '1 BD Coupon'}</option>
                <option value="2_bd_coupon">{isArabic ? 'كوبون 2 دينار' : '2 BD Coupon'}</option>
                <option value="free_session">{isArabic ? 'جلسة مجانية (حتى 2 دينار)' : 'Free Session (Up to 2 BD)'}</option>
                <option value="bogo">{isArabic ? 'اشترِ واحدة واحصل على الثانية' : 'Buy 1 Get 1 Free'}</option>
                <option value="20_percent_discount">{isArabic ? 'خصم 20%' : '20% Discount'}</option>
                <option value="custom">{isArabic ? 'مكافأة مخصصة' : 'Custom Reward'}</option>
              </select>
            </div>

            {/* Custom Reward Value */}
            {rewardForm.reward_type === 'custom' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 700, color: '#232B3E' }}>
                  {isArabic ? 'قيمة المكافأة (بالدينار)' : 'Reward Value (BD)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={rewardForm.reward_value}
                  onChange={(e) => setRewardForm({ ...rewardForm, reward_value: e.target.value })}
                  placeholder={isArabic ? 'أدخل القيمة' : 'Enter value'}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '2px solid #e0eaff',
                    fontSize: 15,
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.border = '2px solid #FF7426'}
                  onBlur={(e) => e.target.style.border = '2px solid #e0eaff'}
                />
              </div>
            )}

            {/* Custom Label */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 700, color: '#232B3E' }}>
                {isArabic ? 'تسمية مخصصة (اختياري)' : 'Custom Label (Optional)'}
              </label>
              <input
                type="text"
                value={rewardForm.reward_label}
                onChange={(e) => setRewardForm({ ...rewardForm, reward_label: e.target.value })}
                placeholder={isArabic ? 'مثال: مكافأة خاصة' : 'e.g. Special Reward'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '2px solid #e0eaff',
                  fontSize: 15,
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.border = '2px solid #FF7426'}
                onBlur={(e) => e.target.style.border = '2px solid #e0eaff'}
              />
            </div>

            {/* Active Status */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={rewardForm.is_active}
                  onChange={(e) => setRewardForm({ ...rewardForm, is_active: e.target.checked })}
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 15, fontWeight: 600, color: '#232B3E' }}>
                  {isArabic ? 'نشط (الطلاب سيحصلون على هذه المكافأة)' : 'Active (Students will receive this reward)'}
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleSaveReward}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#FF7426',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e66620'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#FF7426'}
              >
                <Save size={18} />
                {isArabic ? 'حفظ' : 'Save'}
              </button>
              <button
                onClick={closeRewardModal}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#e0eaff',
                  color: '#232B3E',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#cbd5e1'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#e0eaff'}
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
